import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BarChart3, Plus, Copy, Trash2, Printer, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

// ── Status config ───────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'green',   label: 'Green — On Track',        dot: '#00C26D' },
  { value: 'yellow',  label: 'Yellow — At Risk',         dot: '#F5C400' },
  { value: 'red',     label: 'Red — Off Track',          dot: '#D31010' },
  { value: 'neutral', label: 'Neutral / N/A',            dot: null      },
];

const STATUS_DOT = {
  green:   '#00C26D',
  yellow:  '#F5C400',
  red:     '#D31010',
  neutral: null,
};

// ── Data helpers ────────────────────────────────────────────────────────────

const QUARTERS    = ['Q1', 'Q2', 'Q3', 'Q4'];
const STORAGE_KEY = 'it-metrics-v1';

let _seq = 0;
const uid = () => `m${++_seq}`;

const makeDefaultSections = () => [
  {
    id: 'process', title: 'Process & Service',
    metrics: [
      { id: uid(), name: 'Unplanned Outages',                  subtitle: '',                              targetLabel: 'Target: 0',           actualValue: '1',            status: 'yellow'  },
      { id: uid(), name: 'Time to Restoration',                subtitle: '',                              targetLabel: 'Target: 4 hrs',        actualValue: '~17 hr',       status: 'yellow'  },
      { id: uid(), name: 'Delivery Quality',                   subtitle: '',                              targetLabel: 'Target: TBC',          actualValue: 'Coming Soon!', status: 'neutral' },
      { id: uid(), name: 'IB on IB: Customer Ready Content',   subtitle: '',                              targetLabel: "FY'26 Target: 5",      actualValue: '8',            status: 'green'   },
    ],
  },
  {
    id: 'ai', title: 'AI Innovation',
    metrics: [
      { id: uid(), name: 'AI in IT Use Cases Delivered',       subtitle: '',                              targetLabel: "FY'26 Target: 60",     actualValue: '35',           status: 'green'   },
      { id: uid(), name: 'AI Tool Usage in IT',                subtitle: 'Glean, GitHub Copilot, Claude', targetLabel: 'Target: 90%',          actualValue: '84%',          status: 'yellow'  },
      { id: uid(), name: 'Active AI Experiments',              subtitle: '',                              targetLabel: 'Target: 4',            actualValue: '5',            status: 'green'   },
      { id: uid(), name: 'IT Workflows Transformed by AI',     subtitle: '',                              targetLabel: "FY'26 Target: 8",      actualValue: '3',            status: 'yellow'  },
    ],
  },
  {
    id: 'financial', title: 'Financial',
    metrics: [
      { id: uid(), name: 'IT OpEx % of ARR',                   subtitle: 'includes IT, AI and Infosec',   targetLabel: "FY'29 Target: 4.25",   actualValue: '5.3%',         status: 'yellow'  },
      { id: uid(), name: 'IT Q3 Variance to Forecast',         subtitle: '',                              targetLabel: 'Target: <2%',          actualValue: '3.4%',         status: 'yellow'  },
      { id: uid(), name: 'IT Headcount to Enterprise HC',      subtitle: '',                              targetLabel: 'Target: 1 : 20',       actualValue: '1 : 15',       status: 'yellow'  },
    ],
  },
  {
    id: 'people', title: 'People & Culture',
    metrics: [
      { id: uid(), name: 'Employee Engagement',                subtitle: '',                              targetLabel: "FY'26 Target: 90%",    actualValue: '90%',          status: 'green'   },
      { id: uid(), name: 'QBR – Sentiment',               subtitle: '',                              targetLabel: 'Target: 4.25',         actualValue: '4.34',         status: 'green'   },
      { id: uid(), name: 'Leadership Diversity – Female Directors', subtitle: '',                    targetLabel: "FY'26 Target: 2",      actualValue: '+2',           status: 'green'   },
    ],
  },
];

const makeDefaultData = () =>
  Object.fromEntries(QUARTERS.map(q => [q, { sections: makeDefaultSections() }]));

const loadData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore bad storage */ }
  return makeDefaultData();
};

// ── Module-level section helpers (keeps handler nesting ≤ 4 levels) ────────

const applySectionTitleUpdate = (sections, sectionId, newTitle) =>
  sections.map(s => s.id === sectionId ? { ...s, title: newTitle } : s);

const applySectionMetricSave = (sections, sectionId, isNew, metric, newTitle) =>
  sections.map(s => {
    if (s.id !== sectionId) return s;
    const title = newTitle?.trim() || s.title;
    const metrics = isNew
      ? [...s.metrics, metric]
      : s.metrics.map(m => (m.id === metric.id ? metric : m));
    return { ...s, title, metrics };
  });

const applySectionMetricDelete = (sections, sectionId, metricId) =>
  sections.map(s => {
    if (s.id === sectionId) {
      const removeIdx = s.metrics.findIndex(m => m.id === metricId);
      const metrics = removeIdx === -1
        ? s.metrics
        : [...s.metrics.slice(0, removeIdx), ...s.metrics.slice(removeIdx + 1)];
      return { ...s, metrics };
    }
    return s;
  });

// ── PDF export helper ───────────────────────────────────────────────────────

const exportToPdf = async (el, quarter) => {
  // display:none removes hidden items from flow (visibility:hidden left a gap)
  const toHide = [...el.querySelectorAll('[data-export-hide]')];
  const prevDisplay = toHide.map(n => n.style.display);
  toHide.forEach(n => { n.style.display = 'none'; });

  const htmlEl = document.documentElement;
  const wasDark = htmlEl.classList.contains('dark');
  if (wasDark) htmlEl.classList.remove('dark');
  await new Promise(r => setTimeout(r, 150));

  const canvas = await globalThis.html2canvas(el, {
    scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
    windowWidth: el.scrollWidth,
  });

  if (wasDark) htmlEl.classList.add('dark');
  toHide.forEach((n, i) => { n.style.display = prevDisplay[i]; });

  const W_MM = 338.67;
  const H_MM = 190.5;
  const elWmm = el.offsetWidth  * (25.4 / 96);
  const elHmm = el.offsetHeight * (25.4 / 96);
  const s = Math.min((W_MM - 10) / elWmm, (H_MM - 10) / elHmm, 1);
  const { jsPDF } = globalThis.jspdf;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [W_MM, H_MM] });
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG',
    (W_MM - elWmm * s) / 2, (H_MM - elHmm * s) / 2, elWmm * s, elHmm * s);
  pdf.save(`IT-Scorecard-${quarter}.pdf`);
};

// ── StatusDot helper ────────────────────────────────────────────────────────

const StatusDot = ({ status, size = 'md' }) => {
  const dot = STATUS_DOT[status];
  const dim = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5';
  return (
    <span className={`${dim} rounded-full shrink-0 border inline-block`}
      style={dot
        ? { backgroundColor: dot, borderColor: dot }
        : { backgroundColor: 'transparent', borderColor: '#CBD5E1' }} />
  );
};
StatusDot.propTypes = { status: PropTypes.string.isRequired, size: PropTypes.string };

// ── MetricRow ───────────────────────────────────────────────────────────────

const MetricRow = ({ metric, isLast, onEdit }) => (
  <button
    type="button"
    className={`w-full flex items-center gap-3 px-4 py-3 text-left group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${isLast ? '' : 'border-b border-slate-100 dark:border-slate-800'}`}
    onClick={() => onEdit(metric)}
  >
    {/* Name + subtitle */}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-snug">{metric.name}</p>
      {metric.subtitle && (
        <p className="text-xs text-slate-400 mt-0.5 leading-tight">({metric.subtitle})</p>
      )}
    </div>
    {/* Target / Actual / dot */}
    <div className="flex items-end gap-2 shrink-0">
      <div className="text-right min-w-[90px]">
        <p className="text-[10px] text-slate-400 leading-tight mb-0.5">{metric.targetLabel}</p>
        <p className="text-[15px] font-bold text-slate-900 dark:text-white leading-tight">{metric.actualValue}</p>
      </div>
      <StatusDot status={metric.status} />
    </div>
  </button>
);
MetricRow.propTypes = {
  metric:  PropTypes.object.isRequired,
  isLast:  PropTypes.bool.isRequired,
  onEdit:  PropTypes.func.isRequired,
};

// ── MetricSectionCard ───────────────────────────────────────────────────────

const MetricSectionCard = ({ section, onEditMetric, onAddMetric, onUpdateTitle }) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft,   setTitleDraft]   = useState(section.title);

  const startEdit = () => {
    setTitleDraft(section.title);
    setEditingTitle(true);
  };

  const commitTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== section.title) onUpdateTitle(trimmed);
    setEditingTitle(false);
  };

  const handleTitleKey = (e) => {
    if (e.key === 'Enter') commitTitle();
    if (e.key === 'Escape') setEditingTitle(false);
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden bg-white dark:bg-slate-900 flex flex-col">
      {/* Navy top accent */}
      <div className="h-1.5 bg-[#0F172A] dark:bg-[#00C26D]/60 shrink-0" />

      {/* Header */}
      <div className="bg-[#EEEEF6] dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
        {editingTitle ? (
          <div>
            <label htmlFor={`section-title-${section.id}`} className="sr-only">Section title</label>
            <input
              id={`section-title-${section.id}`}
              autoFocus
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={handleTitleKey}
              className="text-base font-bold text-slate-900 dark:text-white bg-transparent border-b-2 border-[#00C26D] outline-none w-full"
            />
          </div>
        ) : (
          <button type="button" onClick={startEdit} title="Click to rename section"
            className="text-base font-bold text-slate-900 dark:text-white text-left w-full hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
            {section.title}
          </button>
        )}
      </div>

      {/* Metric rows */}
      <div className="flex-1">
        {section.metrics.map((m, i) => (
          <MetricRow
            key={m.id}
            metric={m}
            isLast={i === section.metrics.length - 1}
            onEdit={onEditMetric}
          />
        ))}
      </div>

      {/* Add metric button */}
      <div className="border-t border-slate-100 dark:border-slate-800 shrink-0 print:hidden" data-export-hide>
        <button type="button" onClick={onAddMetric}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs text-slate-400 hover:text-[#00C26D] hover:bg-[#00C26D]/5 transition-colors">
          <Plus className="w-3.5 h-3.5" />Add Metric
        </button>
      </div>
    </div>
  );
};
MetricSectionCard.propTypes = {
  section:        PropTypes.object.isRequired,
  onEditMetric:   PropTypes.func.isRequired,
  onAddMetric:    PropTypes.func.isRequired,
  onUpdateTitle:  PropTypes.func.isRequired,
};

// ── EditMetricModal ─────────────────────────────────────────────────────────

const BLANK = { name: '', subtitle: '', targetLabel: '', actualValue: '', status: 'green' };

const EditMetricModal = ({ metric, sectionTitle, isNew, onSave, onDelete, onClose }) => {
  const [draft, setDraft] = useState({ sectionTitle, ...BLANK, ...metric });
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const canSave = draft.name.trim().length > 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Metric' : 'Edit Metric'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Section title */}
          <div>
            <label htmlFor="em-section" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">
              Section Title
            </label>
            <input id="em-section" value={draft.sectionTitle} onChange={e => set('sectionTitle', e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            <p className="text-[10px] text-slate-400 mt-0.5">Updates the header for this entire section</p>
          </div>

          {/* Metric name */}
          <div>
            <label htmlFor="em-name" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Metric Name</label>
            <input id="em-name" value={draft.name} onChange={e => set('name', e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
          </div>

          {/* Subtitle */}
          <div>
            <label htmlFor="em-sub" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Subtitle / Supporting Text</label>
            <input id="em-sub" value={draft.subtitle} onChange={e => set('subtitle', e.target.value)}
              placeholder="Optional"
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-300" />
          </div>

          {/* Target + Actual side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="em-target" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Target</label>
              <input id="em-target" value={draft.targetLabel} onChange={e => set('targetLabel', e.target.value)}
                placeholder="e.g. Target: 90%"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-300" />
            </div>
            <div>
              <label htmlFor="em-actual" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Actual Value</label>
              <input id="em-actual" value={draft.actualValue} onChange={e => set('actualValue', e.target.value)}
                placeholder="e.g. 84%"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-300" />
            </div>
          </div>

          {/* Status */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Status</legend>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(s => {
                const active = draft.status === s.value;
                return (
                  <label key={s.value}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-all select-none ${
                      active
                        ? 'border-[#00C26D]/50 bg-[#00C26D]/8 text-slate-800 dark:text-white'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                    }`}>
                    <input type="radio" name="em-status" value={s.value} className="sr-only"
                      checked={active} onChange={() => set('status', s.value)} />
                    <StatusDot status={s.value} size="sm" />
                    <span>{s.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* Buttons */}
          <div className="flex items-center gap-2 pt-1">
            {!isNew && (
              <button type="button" onClick={() => onDelete(draft.id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold border border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />Delete
              </button>
            )}
            <div className="flex-1" />
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-bold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={() => onSave(draft)} disabled={!canSave}
              className="px-4 py-2 rounded-lg text-sm font-bold bg-[#00C26D] text-[#1A1A1A] hover:bg-[#00A85D] transition-colors shadow-sm shadow-[#00C26D]/20 disabled:opacity-40 disabled:cursor-not-allowed">
              Save
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
EditMetricModal.propTypes = {
  metric:       PropTypes.object.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  isNew:        PropTypes.bool.isRequired,
  onSave:       PropTypes.func.isRequired,
  onDelete:     PropTypes.func.isRequired,
  onClose:      PropTypes.func.isRequired,
};

// ── ITMetricsDashboardPage ──────────────────────────────────────────────────

const ITMetricsDashboardPage = () => {
  const [data,     setData]     = useState(loadData);
  const [activeQ,  setActiveQ]  = useState('Q1');
  const [editing,  setEditing]  = useState(null);
  const [dupTarget, setDupTarget] = useState('Q2');

  // Auto-save to localStorage on every data change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
  }, [data]);

  const [exporting, setExporting] = useState(false);

  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const el = document.querySelector('[data-testid="it-metrics-dashboard"]');
      if (el) await exportToPdf(el, activeQ);
    } finally {
      setExporting(false);
    }
  };

  // Keep dupTarget valid when activeQ changes
  useEffect(() => {
    if (dupTarget === activeQ) {
      const idx = QUARTERS.indexOf(activeQ);
      setDupTarget(QUARTERS[(idx + 1) % QUARTERS.length]);
    }
  }, [activeQ, dupTarget]);

  const sections = data[activeQ]?.sections ?? [];

  // ── Handlers ──

  const updateSections = (quarter, fn) =>
    setData(prev => ({
      ...prev,
      [quarter]: { ...prev[quarter], sections: fn(prev[quarter].sections) },
    }));

  const handleUpdateTitle = (sectionId, newTitle) =>
    updateSections(activeQ, secs => applySectionTitleUpdate(secs, sectionId, newTitle));

  const handleAddMetric = (sectionId) =>
    setEditing({
      metric: { id: uid(), ...BLANK },
      sectionId,
      isNew: true,
    });

  const handleOpenEdit = (metric, sectionId) =>
    setEditing({ metric, sectionId, isNew: false });

  const handleSave = (saved) => {
    if (!editing) return;
    const { sectionId, isNew } = editing;
    const { sectionTitle: newTitle, ...metric } = saved;
    updateSections(activeQ, secs => applySectionMetricSave(secs, sectionId, isNew, metric, newTitle));
    setEditing(null);
    toast.success(isNew ? 'Metric added' : 'Metric saved');
  };

  const handleDelete = (metricId) => {
    if (!editing) return;
    const { sectionId } = editing;
    updateSections(activeQ, secs => applySectionMetricDelete(secs, sectionId, metricId));
    setEditing(null);
    toast.success('Metric deleted');
  };

  const handleDuplicate = () => {
    setData(prev => ({
      ...prev,
      [dupTarget]: structuredClone(prev[activeQ]),
    }));
    toast.success(`Copied ${activeQ} → ${dupTarget}`);
  };

  const editingSection = editing ? sections.find(s => s.id === editing.sectionId) : null;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="it-metrics-dashboard">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-[#00C26D]/10 dark:bg-[#00C26D]/15 rounded-xl flex items-center justify-center shrink-0">
            <BarChart3 className="w-6 h-6 text-[#00995A] dark:text-[#00D97E]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">IT Scorecard</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400" data-export-hide>Click any metric row to edit &middot; auto-saved per quarter</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0 print:hidden" data-export-hide>
          {/* Export to PDF */}
          <button type="button" onClick={handleExportPDF} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium transition-colors border border-slate-200 dark:border-slate-700 disabled:opacity-60">
            {exporting
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Exporting…</>
              : <><Printer className="w-3.5 h-3.5" />Export PDF</>}
          </button>

          {/* Quarter selector */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {QUARTERS.map(q => (
              <button key={q} type="button" onClick={() => setActiveQ(q)}
                className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${
                  activeQ === q
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}>
                {q}
              </button>
            ))}
          </div>

          {/* Duplicate quarter */}
          <div className="flex items-center gap-2">
            <label htmlFor="dup-target" className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
              Copy {activeQ} →
            </label>
            <select id="dup-target" value={dupTarget} onChange={e => setDupTarget(e.target.value)}
              className="rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40">
              {QUARTERS.filter(q => q !== activeQ).map(q => <option key={q} value={q}>{q}</option>)}
            </select>
            <button type="button" onClick={handleDuplicate}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium transition-colors border border-slate-200 dark:border-slate-700">
              <Copy className="w-3.5 h-3.5" />Copy
            </button>
          </div>
        </div>
      </div>

      {/* ── 2×2 grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {sections.map(section => (
          <MetricSectionCard
            key={section.id}
            section={section}
            onEditMetric={metric => handleOpenEdit(metric, section.id)}
            onAddMetric={() => handleAddMetric(section.id)}
            onUpdateTitle={title => handleUpdateTitle(section.id, title)}
          />
        ))}
      </div>

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
        {STATUS_OPTIONS.filter(s => s.value !== 'neutral').map(s => (
          <div key={s.value} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: s.dot }} />
            {s.label}
          </div>
        ))}
      </div>

      {/* ── Edit / Add modal ── */}
      {editing && editingSection && (
        <EditMetricModal
          metric={editing.metric}
          sectionTitle={editingSection.title}
          isNew={editing.isNew}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default ITMetricsDashboardPage;
